import type { ClientLogEntry } from "@/types/ClientLog"
import { ClientEventType } from "@/types/ClientLog"
import { API_URL } from "@/utils/env"

export { ClientEventType }

export default class ClientLogger {
  private readonly maxBufferSize: number
  private readonly flushIntervalMs: number

  private buffer: ClientLogEntry[] = []
  private username: string | null = null
  private flushTimer: ReturnType<typeof setInterval> | null = null
  private consecutiveFailures = 0
  private readonly maxConsecutiveFailures = 3
  private readonly backpressureLimit = 500
  private _isInitialized = false

  constructor(maxBufferSize = 100, flushIntervalMs = 10_000) {
    this.maxBufferSize = maxBufferSize
    this.flushIntervalMs = flushIntervalMs
  }

  // ─── Lifecycle ──────────────────────────────────────────────────────

  init(username: string): void {
    this.username = username
    this._isInitialized = true
    this.consecutiveFailures = 0

    this.flushTimer = setInterval(() => {
      if (this.buffer.length > 0) {
        this.flush()
      }
    }, this.flushIntervalMs)

    if (typeof window !== "undefined") {
      window.addEventListener("visibilitychange", this.handleVisibilityChange)
      window.addEventListener("beforeunload", this.handleBeforeUnload)
    }
  }

  destroy(): void {
    if (this.buffer.length > 0) {
      this.flush()
    }

    if (this.flushTimer) {
      clearInterval(this.flushTimer)
      this.flushTimer = null
    }

    if (typeof window !== "undefined") {
      window.removeEventListener("visibilitychange", this.handleVisibilityChange)
      window.removeEventListener("beforeunload", this.handleBeforeUnload)
    }

    this.buffer = []
    this.username = null
    this._isInitialized = false
  }

  get isInitialized(): boolean {
    return this._isInitialized
  }

  // ─── Logging ────────────────────────────────────────────────────────

  log(eventType: ClientEventType, metadata: Record<string, unknown> = {}): void {
    if (!this._isInitialized || !this.username) return

    const entry: ClientLogEntry = {
      eventType,
      timestamp: new Date().toISOString(),
      username: this.username,
      metadata,
    }

    this.buffer.push(entry)

    if (this.buffer.length >= this.maxBufferSize) {
      this.flush()
    }
  }

  // ─── Flush ──────────────────────────────────────────────────────────

  async flush(): Promise<void> {
    if (this.buffer.length === 0) return

    const batch = this.buffer.splice(0, this.maxBufferSize)

    try {
      // Use customInstance from Orval custom-fetch (dynamic import to avoid circular deps)
      const { customInstance } = await import("@/api/custom-fetch")
      const url = new URL("api/client-logs", API_URL).toString()

      await customInstance(url, {
        method: "POST",
        body: JSON.stringify({ logs: batch }),
      })

      this.consecutiveFailures = 0
    } catch {
      this.handleFlushFailure(batch)
    }
  }

  // ─── Internals ──────────────────────────────────────────────────────

  private handleFlushFailure(failedBatch: ClientLogEntry[]): void {
    this.consecutiveFailures++

    if (this.consecutiveFailures < this.maxConsecutiveFailures) {
      this.buffer.unshift(...failedBatch)
    }

    if (this.buffer.length > this.backpressureLimit) {
      this.buffer = this.buffer.slice(-this.backpressureLimit)
    }
  }

  private handleVisibilityChange = (): void => {
    if (document.visibilityState === "hidden" && this.buffer.length > 0) {
      this.flush()
    }
  }

  private handleBeforeUnload = (): void => {
    if (this.buffer.length === 0) return

    const batch = this.buffer.splice(0, this.maxBufferSize)
    const payload = JSON.stringify({ logs: batch })

    try {
      const url = new URL("api/client-logs", API_URL).toString()
      navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }))
    } catch {
      // Best-effort — acceptable to lose logs on page close
    }
  }
}
